import { Component } from 'react';
import { connect } from 'react-redux';
import { asField } from 'informed';
import { Icon } from 'components/common/icons';

const Text = connect((state) => ({
  messages: state.messages,
  theme: state.theme
}))(
  asField(
    class extends Component {
      state = {
        asyncError: null,
        success: false,
        validating: false
      };

      componentDidUpdate(prevProps) {
        if (this.props.fieldState.value === undefined && this.state.success) {
          this.setState({ success: false });
        }
        // form submission causes field error from customValidateAsync to reset
        // this is a workaround to prevent this
        if (prevProps.fieldState.error && !this.props.fieldState.error) {
          if (this.state.asyncError && !this.state.validating) {
            this.props.fieldApi.setError(this.state.asyncError);
          }
        }
      }

      handleBlur = () => {
        const { fieldApi } = this.props;
        const value = fieldApi.getValue();
        if (value && value.trim) fieldApi.setValue(value.trim());
      };

      handleChange = async (ev) => {
        const {
          customValidateAsync,
          fieldApi,
          maxLength,
          messages,
          onChange,
          required,
          type
        } = this.props;

        let value = ev.target.value;
        if (type === 'number') value = parseInt(value, 10);

        if (!maxLength || value?.length <= maxLength) fieldApi.setValue(value);
        fieldApi.setTouched(true);

        if (onChange) onChange(value, ev);

        if (value) {
          fieldApi.setError(undefined);
        } else if (!value && required) {
          fieldApi.setError(messages.commonFieldRequired);
        }

        if (customValidateAsync) {
          this.setState({ validating: true });
          const error = await customValidateAsync(value);
          fieldApi.setError(error);
          this.setState({
            asyncError: error,
            success: !error,
            validating: false
          });
        }
      };

      render() {
        const {
          customValidateAsync,
          darkMode,
          dispatch,
          field,
          fieldApi,
          fieldState,
          forwardedRef,
          iconStyle,
          id,
          infoMessage,
          initialValue,
          inputContainerRef,
          inputStyle,
          loading,
          maxLength,
          messages,
          onChange,
          placeholder,
          showErrorIcon = true,
          showErrorMessage = true,
          style,
          theme = {},
          type,
          ...params
        } = this.props;

        let color = theme.textColor;
        let backgroundColor = '#fff';
        let backgroundColorDisabled = theme.lightGreyLighter;
        let borderColor = theme.lightGreyDark;
        let borderColorActive = '#a1a8aa';
        let errorColor = theme.dangerColor;
        let infoColor = theme.textColorLighter;
        let placeholderColor = '#999';
        if (darkMode) {
          color = '#fff';
          backgroundColor = 'rgba(0, 0, 0, 0.2)';
          backgroundColorDisabled = 'rgba(255, 255, 255, 0.2)';
          borderColor = 'rgba(255, 255, 255, 0.3)';
          borderColorActive = 'rgba(255, 255, 255, 0.6)';
          errorColor = theme.dangerColor;
          infoColor = '#ddd';
          placeholderColor = '#ccc';
        }

        const error = fieldState.error;

        return (
          <div className="wrapper" ref={inputContainerRef} style={style}>
            <input
              {...params}
              className="input"
              id={id}
              onBlur={this.handleBlur}
              onChange={this.handleChange}
              placeholder={placeholder}
              style={inputStyle}
              type={type || 'text'}
              value={
                !fieldState.value && fieldState.value !== 0
                  ? ''
                  : fieldState.value
              }
            />

            {maxLength ? (
              <div className="character-limit">
                {fieldState.value
                  ? maxLength - (fieldState.value?.length || 0)
                  : maxLength}
                {` ${messages.commonCharactersRemaining}`}
              </div>
            ) : null}

            {error && showErrorIcon ? (
              <div className="icon" style={iconStyle}>
                <Icon color={theme.dangerColor} icon="exclamation-circle" />
              </div>
            ) : loading || this.state.validating ? (
              <div className="icon" style={iconStyle}>
                <span className="loading" />
              </div>
            ) : this.state.success ? (
              <div className="icon" style={iconStyle}>
                <Icon
                  color={theme.successStandaloneColor}
                  icon="check-circle"
                />
              </div>
            ) : null}

            {error && showErrorMessage ? (
              <div className="error-message">{error}</div>
            ) : null}

            {infoMessage ? (
              <div className="info-message">{infoMessage}</div>
            ) : null}

            <style jsx>{`
              .wrapper {
                position: relative;
              }
              .input {
                background-color: ${backgroundColor};
                border: 1px solid ${error ? errorColor : borderColor};
                border-radius: ${theme.inputBorderRadius};
                color: ${color};
                font-family: inherit;
                font-size: 1.5rem;
                outline: 0;
                padding: 7px 10px;
                position: relative;
                transition: border-color 0.2s;
                width: 100%;
              }
              .input:focus {
                border-color: ${error ? errorColor : borderColorActive};
              }
              .input:disabled {
                background-color: ${backgroundColorDisabled};
                color: ${color};
                opacity: 0.7;
              }
              .input::-webkit-input-placeholder {
                color: ${placeholderColor};
                opacity: 1;
              }
              .input::-moz-placeholder {
                color: ${placeholderColor};
                opacity: 1;
              }
              .input::-ms-input-placeholder {
                color: ${placeholderColor};
                opacity: 1;
              }
              .input:-ms-input-placeholder {
                color: ${placeholderColor};
                opacity: 1;
              }
              @keyframes loading {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
              .icon {
                line-height: 0;
                position: absolute;
                right: 12px;
                top: 9px;
              }
              .character-limit {
                color: ${infoColor};
                font-size: 1.3rem;
                font-weight: 400;
                padding: 3px;
              }
              .loading {
                animation: loading 1.2s infinite linear;
                border-color: #bcd;
                border-style: solid;
                border-radius: 14px;
                border-top-color: transparent;
                border-width: 1px;
                display: inline-block;
                margin-top: 1px;
                height: 14px;
                width: 14px;
              }
              .error-message {
                font-size: 1.3rem;
                font-weight: 400;
                color: ${errorColor};
                padding: 3px;
              }
              .info-message {
                color: ${infoColor};
                font-size: 1.3rem;
                font-weight: 400;
                padding: 3px;
              }
            `}</style>
          </div>
        );
      }
    }
  )
);

export default Text;
